/**
 * components/client-detail/DossierBuilderModal.tsx
 *
 * Fullscreen modal for the "Dossier de pièces" workflow.
 * Single-screen 3-column layout - no wizard, no steps:
 *  Left   : DossierDocSidebar - catalog of all documents with inclusion toggles
 *  Center : DossierMainContent - sortable included-pieces list + failed-conversion section
 *  Right  : DossierActionSidebar - stamp toggle + 3 independent download cards
 *
 * Document preview (DocumentPreviewModal) is managed here and shared by both sidebars.
 * PDF buffers are preloaded on mount in the background for near-instant generation.
 * Escape key: closes preview if open, otherwise closes the modal.
 *
 * Updated Apr 2026: replaced 2-step wizard with single-screen 3-column layout.
 * Removed FormWizardStepper, FormWizardFooter, step state, and transition animations.
 * Each download (dossier PDF, bordereau PDF, bordereau Word) is fully independent.
 * Updated Apr 2026: removed handleClose useCallback wrapper (redundant passthrough).
 * Updated Apr 2026: preloadBuffers effect now waits for includedPieces to be populated
 *   (mount-time call was a no-op since pieces start empty); hasPreloadedRef guards against
 *   repeated calls when includedPieces subsequently changes.
 * Updated Apr 2026: wired moveDoc and reorderDocsInPiece from hook for per-file reordering.
 * Updated Jun 2026: accepts availableFields and onRefresh props; wires onReclassify and
 *   onRename through to DossierMainContent for lawyer-side document correction.
 */

import { useEffect, useRef, useState } from 'react';
import { X, FolderOpen } from 'lucide-react';
import { useDossierBuilder } from '../../hooks/useDossierBuilder';
import { useFocusTrap } from '../../hooks/useFocusTrap';
import { useDocumentOverride } from '../../hooks/useDocumentOverride';
import DossierDocSidebar from './DossierDocSidebar';
import DossierMainContent from './DossierMainContent';
import DossierActionSidebar from './DossierActionSidebar';
import DocumentPreviewModal from './DocumentPreviewModal';
import type { DocumentEntry } from '../../types/dossier';
import type { AvailableField } from './ClientDocumentsTab';

interface DossierBuilderModalProps {
  documents: DocumentEntry[];
  clientName: string | null;
  availableFields: AvailableField[];
  onClose: () => void;
  onRefresh: () => void;
}

export default function DossierBuilderModal({
  documents,
  clientName,
  availableFields,
  onClose,
  onRefresh,
}: DossierBuilderModalProps) {
  const { reclassifyDocument, renameDocument } = useDocumentOverride(onRefresh);
  const {
    pieces,
    includedPieces,
    excludedDocIds,
    failedDocs,
    withStamp,
    stampStatus,
    dossierPdfPhase,
    dossierPdfProgress,
    bordereauPdfPhase,
    bordereauDocPhase,
    retryingDocIds,
    isPreloading,
    updateLabel,
    movePiece,
    reorderPieces,
    moveDoc,
    reorderDocsInPiece,
    handleToggleStamp,
    excludeDoc,
    includeDoc,
    retryDoc,
    preloadBuffers,
    downloadDossierPdf,
    downloadBordereauPdf,
    downloadBordereauDoc,
  } = useDossierBuilder(documents, clientName);

  const modalRef = useRef<HTMLDivElement>(null);
  const hasPreloadedRef = useRef(false);
  const [previewDoc, setPreviewDoc] = useState<DocumentEntry | null>(null);

  useFocusTrap(modalRef, true);

  useEffect(() => {
    if (hasPreloadedRef.current) return;
    if (includedPieces.length === 0) return;
    hasPreloadedRef.current = true;
    preloadBuffers();
  }, [preloadBuffers, includedPieces.length]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (previewDoc) setPreviewDoc(null);
        else onClose();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, previewDoc]);

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  const sidebarPieces = [...pieces].sort((a, b) => a.fieldPosition - b.fieldPosition);

  const previewPath = previewDoc ? (previewDoc.pdfPath ?? previewDoc.storagePath) : '';
  const previewMime = previewDoc
    ? previewDoc.pdfPath
      ? 'application/pdf'
      : previewDoc.mimeType
    : '';
  const previewName = previewDoc
    ? (previewDoc.displayFilename ?? previewDoc.originalFilename)
    : '';

  return (
    <div
      ref={modalRef}
      role="dialog"
      aria-modal="true"
      aria-labelledby="dossier-title"
      className="fixed inset-0 z-50 bg-neutral-100 flex flex-col"
    >
      <header className="bg-white flex-shrink-0 border-b border-neutral-200">
        <div className="px-5 py-3 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0">
            <div className="flex items-center justify-center w-8 h-8 bg-primary-50 rounded-xl shrink-0">
              <FolderOpen className="w-4 h-4 text-primary-600" />
            </div>
            <div className="flex items-center gap-2.5 min-w-0">
              <h1
                id="dossier-title"
                className="text-base font-semibold text-neutral-900 whitespace-nowrap"
              >
                Dossier de pièces
              </h1>
              {clientName && (
                <>
                  <span className="text-neutral-300 hidden sm:inline">·</span>
                  <span className="text-sm text-neutral-500 truncate hidden sm:inline">
                    {clientName}
                  </span>
                </>
              )}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-2 rounded-xl text-neutral-400 hover:text-neutral-700 hover:bg-neutral-100 transition-all flex-shrink-0"
            aria-label="Fermer"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      </header>

      <div className="flex-1 overflow-hidden flex">
        <DossierDocSidebar
          pieces={sidebarPieces}
          excludedDocIds={excludedDocIds}
          retryingDocIds={retryingDocIds}
          onIncludeDoc={includeDoc}
          onExcludeDoc={excludeDoc}
          onPreviewDoc={setPreviewDoc}
          onRetryDoc={retryDoc}
        />

        <DossierMainContent
          pieces={pieces}
          includedPieces={includedPieces}
          availableFields={availableFields}
          failedDocs={failedDocs}
          retryingDocIds={retryingDocIds}
          onLabelChange={updateLabel}
          onMove={movePiece}
          onReorder={reorderPieces}
          onExcludeDoc={excludeDoc}
          onPreviewDoc={setPreviewDoc}
          onRetry={retryDoc}
          onReorderDocs={reorderDocsInPiece}
          onMoveDoc={moveDoc}
          onReclassify={reclassifyDocument}
          onRename={renameDocument}
        />

        <DossierActionSidebar
          includedPieces={includedPieces}
          withStamp={withStamp}
          stampStatus={stampStatus}
          isPreloading={isPreloading}
          dossierPdfPhase={dossierPdfPhase}
          dossierPdfProgress={dossierPdfProgress}
          bordereauPdfPhase={bordereauPdfPhase}
          bordereauDocPhase={bordereauDocPhase}
          onToggleStamp={handleToggleStamp}
          onDownloadDossierPdf={downloadDossierPdf}
          onDownloadBordereauPdf={downloadBordereauPdf}
          onDownloadBordereauDoc={downloadBordereauDoc}
        />
      </div>

      {previewDoc && (
        <DocumentPreviewModal
          storagePath={previewPath}
          filename={previewName}
          mimeType={previewMime}
          onClose={() => setPreviewDoc(null)}
          showDownload={false}
        />
      )}
    </div>
  );
}
